/**
 * V38.05 — Panneau de gestion Newsletter (Super Admin).
 *
 * Sous-onglets : Abonnés / Campagnes / Statistiques.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Mail, Send, Trash2, Plus, Loader2, BarChart3, Users, CheckCircle2, Pencil, FileText } from 'lucide-react';
import { NewsletterCampaignDialog } from './NewsletterCampaignDialog';
import { NewsletterSubscriberDialog } from './NewsletterSubscriberDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
        || localStorage.getItem('access_token')
        || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR'); } catch { return iso; }
};

const EMPTY_CAMPAIGN = {
  subject: '',
  html: '',
  plain_text: '',
  sender_email: '',
  sender_name: '',
  target_sources: [],
  target_territoires: [],
  target_tags: [],
};

export default function NewsletterPanel() {
  const [tab, setTab] = useState('subscribers');
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [segments, setSegments] = useState({ sources: [], territoires: [], tags: [] });
  const [segmentPreview, setSegmentPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyActive, setOnlyActive] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendTo, setSendTo] = useState('');
  // V38.06 — Édition abonné (territoire + tags)
  const [editingSub, setEditingSub] = useState(null);
  const [savingSub, setSavingSub] = useState(false);
  // Filtres tableau abonnés
  const [filterSource, setFilterSource] = useState('');
  const [filterTerritoire, setFilterTerritoire] = useState('');
  const [filterTag, setFilterTag] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const subQs = new URLSearchParams({
        only_active: String(onlyActive),
        limit: '500',
      });
      if (filterSource) subQs.append('source', filterSource);
      if (filterTerritoire) subQs.append('territoire', filterTerritoire);
      if (filterTag) subQs.append('tag', filterTag);
      const [subRes, campRes, statsRes, segRes] = await Promise.all([
        axios.get(`${API}/admin/newsletter/subscribers?${subQs}`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/newsletter/campaigns`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/newsletter/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/newsletter/segments`, { headers: getAuthHeader() }),
      ]);
      setSubscribers(subRes.data?.subscribers || []);
      setCampaigns(campRes.data?.campaigns || []);
      setStats(statsRes.data || null);
      setSegments({
        sources: segRes.data?.sources || [],
        territoires: segRes.data?.territoires || [],
        tags: segRes.data?.tags || [],
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement newsletter');
    } finally {
      setLoading(false);
    }
  }, [onlyActive, filterSource, filterTerritoire, filterTag]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // V38.06 — Prévisualisation du segment ciblé pour la campagne en cours d'édition.
  useEffect(() => {
    if (!editing) { setSegmentPreview(null); return; }
    const ts = editing.target_sources || [];
    const tt = editing.target_territoires || [];
    const tg = editing.target_tags || [];
    if (ts.length === 0 && tt.length === 0 && tg.length === 0) {
      setSegmentPreview({ count: stats?.subscribers_active ?? 0, all: true });
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/admin/newsletter/segments/preview`, {
          target_sources: ts, target_territoires: tt, target_tags: tg,
        }, { headers: getAuthHeader() });
        if (!cancel) setSegmentPreview({ count: res.data?.count ?? 0, all: false });
      } catch { /* silencieux */ }
    })();
    return () => { cancel = true; };
  }, [editing?.target_sources, editing?.target_territoires, editing?.target_tags, stats?.subscribers_active, editing]);

  const handleDeleteSubscriber = async (sub) => {
    if (!window.confirm(`Supprimer définitivement l'abonné ${sub.email} ?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter/subscribers/${sub.id}`, { headers: getAuthHeader() });
      toast.success('Abonné supprimé');
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleEditSubscriber = (sub) => {
    setEditingSub({
      id: sub.id,
      email: sub.email,
      name: sub.name || '',
      territoire: sub.territoire || '',
      tags: sub.tags || [],
      _tagInput: '',
    });
  };

  const handleSaveSubscriber = async () => {
    if (!editingSub) return;
    setSavingSub(true);
    try {
      const payload = {
        name: editingSub.name || null,
        territoire: editingSub.territoire || null,
        tags: editingSub.tags || [],
      };
      await axios.put(`${API}/admin/newsletter/subscribers/${editingSub.id}`, payload, { headers: getAuthHeader() });
      toast.success('Abonné mis à jour');
      setEditingSub(null);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSavingSub(false);
    }
  };

  const openCreateCampaign = () => {
    setEditing({ ...EMPTY_CAMPAIGN, _mode: 'create' });
  };

  const openEditCampaign = (camp) => {
    setEditing({
      _mode: 'edit',
      id: camp.id,
      subject: camp.subject,
      html: camp.html,
      plain_text: camp.plain_text || '',
      sender_email: camp.sender_email || '',
      sender_name: camp.sender_name || '',
      target_sources: camp.target_sources || [],
      target_territoires: camp.target_territoires || [],
      target_tags: camp.target_tags || [],
      status: camp.status,
    });
  };

  const handleSaveCampaign = async () => {
    if (!editing) return;
    if (!editing.subject?.trim() || !editing.html?.trim()) {
      toast.error('Sujet et corps HTML requis');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        subject: editing.subject.trim(),
        html: editing.html,
        plain_text: (editing.plain_text || '').trim() || null,
        sender_email: editing.sender_email || null,
        sender_name: editing.sender_name || null,
        target_sources: editing.target_sources || [],
        target_territoires: editing.target_territoires || [],
        target_tags: editing.target_tags || [],
      };
      if (editing._mode === 'create') {
        await axios.post(`${API}/admin/newsletter/campaigns`, payload, { headers: getAuthHeader() });
        toast.success('Campagne créée');
      } else {
        await axios.put(`${API}/admin/newsletter/campaigns/${editing.id}`, payload, { headers: getAuthHeader() });
        toast.success('Campagne mise à jour');
      }
      setEditing(null);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCampaign = async (camp) => {
    if (!window.confirm(`Supprimer la campagne « ${camp.subject} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter/campaigns/${camp.id}`, { headers: getAuthHeader() });
      toast.success('Campagne supprimée');
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleSendCampaign = async (camp, testMode = false) => {
    const payload = { test_mode: testMode, only_to: testMode ? sendTo.split(',').map((e) => e.trim()).filter(Boolean) : null };
    const msg = testMode
      ? `Envoyer un test à ${(payload.only_to || []).join(', ')} ?`
      : `Envoyer la campagne « ${camp.subject} » à tous les abonnés actifs ?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.post(`${API}/admin/newsletter/campaigns/${camp.id}/send`, payload, { headers: getAuthHeader() });
      toast.success(`Envois OK : ${res.data?.sent_ok} | Échecs : ${res.data?.sent_fail}`);
      setSendTo('');
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur envoi');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="newsletter-loading">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <Card data-testid="newsletter-panel">
      <CardHeader className="border-b">
        <CardTitle className="flex items-center gap-2 text-emerald-900">
          <Mail className="h-5 w-5" />
          Newsletter — Gestion
        </CardTitle>
        <CardDescription>
          Inscriptions publiques, campagnes Brevo et statistiques d'engagement.
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="subscribers" data-testid="newsletter-tab-subscribers">
              <Users className="h-4 w-4 mr-1" /> Abonnés ({subscribers.length})
            </TabsTrigger>
            <TabsTrigger value="campaigns" data-testid="newsletter-tab-campaigns">
              <Send className="h-4 w-4 mr-1" /> Campagnes ({campaigns.length})
            </TabsTrigger>
            <TabsTrigger value="stats" data-testid="newsletter-tab-stats">
              <BarChart3 className="h-4 w-4 mr-1" /> Statistiques
            </TabsTrigger>
          </TabsList>

          {/* ABONNES */}
          <TabsContent value="subscribers" className="space-y-3 pt-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant={onlyActive ? 'default' : 'outline'}
                onClick={() => setOnlyActive(true)}
                data-testid="newsletter-filter-active"
              >
                Actifs uniquement
              </Button>
              <Button
                size="sm"
                variant={!onlyActive ? 'default' : 'outline'}
                onClick={() => setOnlyActive(false)}
                data-testid="newsletter-filter-all"
              >
                Tous (incl. désinscrits)
              </Button>
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="text-xs border rounded px-2 py-1"
                data-testid="newsletter-filter-source"
              >
                <option value="">Toutes sources</option>
                {segments.sources.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={filterTerritoire}
                onChange={(e) => setFilterTerritoire(e.target.value)}
                className="text-xs border rounded px-2 py-1"
                data-testid="newsletter-filter-territoire"
              >
                <option value="">Tous territoires</option>
                {segments.territoires.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                className="text-xs border rounded px-2 py-1"
                data-testid="newsletter-filter-tag"
              >
                <option value="">Tous tags</option>
                {segments.tags.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {(filterSource || filterTerritoire || filterTag) && (
                <Button size="sm" variant="ghost" onClick={() => { setFilterSource(''); setFilterTerritoire(''); setFilterTag(''); }} data-testid="newsletter-filter-clear">
                  Réinitialiser
                </Button>
              )}
            </div>
            {subscribers.length === 0 ? (
              <p className="italic text-slate-500">Aucun abonné.</p>
            ) : (
              <div className="border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs">
                    <tr>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Nom</th>
                      <th className="px-3 py-2">Source</th>
                      <th className="px-3 py-2">Territoire</th>
                      <th className="px-3 py-2">Tags</th>
                      <th className="px-3 py-2">Inscription</th>
                      <th className="px-3 py-2">Statut</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((s) => (
                      <tr key={s.id} className="border-t" data-testid={`newsletter-subscriber-row-${s.email}`}>
                        <td className="px-3 py-2 font-mono text-[11px]">{s.email}</td>
                        <td className="px-3 py-2">{s.name || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{s.source || '—'}</td>
                        <td className="px-3 py-2 text-xs">{s.territoire || '—'}</td>
                        <td className="px-3 py-2 text-xs">
                          {(s.tags || []).length === 0 ? '—' : (
                            <div className="flex flex-wrap gap-1">
                              {(s.tags || []).map((t) => (
                                <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{fmtDate(s.subscribed_at)}</td>
                        <td className="px-3 py-2">
                          {s.unsubscribed_at ? (
                            <Badge variant="outline" className="text-red-700 border-red-300">Désinscrit</Badge>
                          ) : (
                            <Badge className="bg-emerald-600 text-white font-semibold">Actif</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Button size="sm" variant="outline" onClick={() => handleEditSubscriber(s)}
                                  data-testid={`newsletter-edit-subscriber-${s.email}`} className="mr-1">
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteSubscriber(s)}
                                  className="text-red-700 hover:bg-red-50"
                                  data-testid={`newsletter-delete-subscriber-${s.email}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* CAMPAGNES */}
          <TabsContent value="campaigns" className="space-y-3 pt-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Campagnes</h4>
              <Button onClick={openCreateCampaign} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="newsletter-create-campaign-btn">
                <Plus className="h-4 w-4 mr-1" /> Nouvelle campagne
              </Button>
            </div>

            {campaigns.length === 0 ? (
              <p className="italic text-slate-500">Aucune campagne.</p>
            ) : campaigns.map((c) => (
              <div key={c.id} className="border rounded p-3 flex flex-wrap items-start gap-3" data-testid={`newsletter-campaign-row-${c.id}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold">{c.subject}</span>
                    {c.status === 'sent' ? (
                      <Badge className="bg-emerald-600 text-white font-semibold"><CheckCircle2 className="h-3 w-3 mr-0.5" />Envoyée</Badge>
                    ) : (
                      <Badge variant="outline">Brouillon</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Créée : {fmtDate(c.created_at)} {c.sent_at ? `• Envoyée : ${fmtDate(c.sent_at)}` : ''}
                  </p>
                  <p className="text-xs mt-1">
                    Destinataires : <b>{c.recipients_count || 0}</b>
                    {' '}• Ouvertures : <b className="text-emerald-700">{c.opened || 0}</b>
                    {' '}• Clics : <b className="text-blue-700">{c.clicked || 0}</b>
                    {' '}• Bounces : <b className="text-red-700">{c.bounced || 0}</b>
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  {c.status !== 'sent' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEditCampaign(c)}
                              data-testid={`newsletter-edit-campaign-${c.id}`}>
                        <Pencil className="h-3 w-3 mr-1" />Éditer
                      </Button>
                      <Button size="sm" onClick={() => handleSendCampaign(c, false)}
                              className="bg-emerald-700 hover:bg-emerald-800 text-white"
                              data-testid={`newsletter-send-campaign-${c.id}`}>
                        <Send className="h-3 w-3 mr-1" />Envoyer
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => handleDeleteCampaign(c)}
                          className="text-red-700 hover:bg-red-50"
                          data-testid={`newsletter-delete-campaign-${c.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>

                {c.status !== 'sent' && (
                  <div className="w-full flex gap-2 mt-2">
                    <Input
                      placeholder="Emails de test (séparés par virgule)"
                      value={sendTo}
                      onChange={(e) => setSendTo(e.target.value)}
                      className="text-xs"
                      data-testid="newsletter-test-emails-input"
                    />
                    <Button size="sm" variant="outline" onClick={() => handleSendCampaign(c, true)}
                            disabled={!sendTo.trim()}
                            data-testid={`newsletter-test-send-${c.id}`}>
                      Test
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </TabsContent>

          {/* STATS */}
          <TabsContent value="stats" className="pt-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="newsletter-stats-grid">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500">Abonnés actifs</p>
                  <p className="text-3xl font-bold text-emerald-700">{stats?.subscribers_active ?? 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500">Désinscrits</p>
                  <p className="text-3xl font-bold text-slate-700">{stats?.subscribers_unsubscribed ?? 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500">Campagnes</p>
                  <p className="text-3xl font-bold text-blue-700">{stats?.campaigns_total ?? 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-slate-500">Campagnes envoyées</p>
                  <p className="text-3xl font-bold text-amber-700">{stats?.campaigns_sent ?? 0}</p>
                </CardContent>
              </Card>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              Les statistiques d'ouverture / clics sont alimentées en temps réel
              par le webhook Brevo (<code>/api/newsletter/brevo-webhook</code>).
            </p>
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Dialog création / édition campagne */}
      <NewsletterCampaignDialog
        editing={editing}
        setEditing={setEditing}
        saving={saving}
        onSave={handleSaveCampaign}
        segments={segments}
        segmentPreview={segmentPreview}
      />
      {/* V38.06 — Dialog édition abonné (territoire + tags) */}
      <NewsletterSubscriberDialog
        editingSub={editingSub}
        setEditingSub={setEditingSub}
        savingSub={savingSub}
        onSave={handleSaveSubscriber}
      />
    </Card>
  );
}
